import { Route, Navigate } from 'react-router-dom';
import AdminDashboard from '@/components/admin/dashboard/AdminDashboard';
import AdminUsersList from '@/components/admin/users/AdminUsersList';
import AdminSessionsList from '@/components/admin/chats/AdminSessionsList';
import AdminLLMToolsList from '@/components/admin/tools/AdminLLMToolsList';
import AdminChatWindow from '@/components/admin/chats/AdminChatWindow';
import AdminJobsList from '@/components/admin/jobs/AdminJobsList';
import AdminJobDetail from '@/components/admin/jobs/AdminJobDetail';
import AdminJobLogs from '@/components/admin/jobs/AdminJobLogs';
import AdminErrorsList from '@/components/admin/errors/AdminErrorsList';
import AdminSymbolsList from '@/components/admin/symbols/AdminSymbolsList';
import AdminSymbolDetail from '@/components/admin/symbols/AdminSymbolDetail';
import AdminStockExchangesList from '@/components/admin/stock-exchanges/AdminStockExchangesList';
import AdminTradingRecommendationsList from '@/components/admin/recommendations/AdminTradingRecommendationsList';
import AdminCoreRecommendationFullDetail from '@/components/admin/recommendations/AdminCoreRecommendationFullDetail';
import AdminEarningsRecommendationFullDetail from '@/components/admin/recommendations/AdminEarningsRecommendationFullDetail';
import AdminSwingTradeFullDetail from '@/components/admin/recommendations/AdminSwingTradeFullDetail';
import AdminLongTermTradeFullDetail from '@/components/admin/recommendations/AdminLongTermTradeFullDetail';
import AdminCoursesList from '@/components/admin/academy/AdminCoursesList';
import AdminCourseDetail from '@/components/admin/academy/AdminCourseDetail';
import AdminAllCoursesList from '@/components/admin/academy/AdminAllCoursesList';
import AdminAllModulesList from '@/components/admin/academy/AdminAllModulesList';
import AdminAllVideosList from '@/components/admin/academy/AdminAllVideosList';
import AdminToolsPage from '@/components/admin/tools/AdminToolsPage';
import CourseMailSequence from '@/components/admin/academy/CourseMailSequence';
import AdminModuleDetail from '@/components/admin/academy/AdminModuleDetail';
import AdminMailsList from '@/components/admin/mails/AdminMailsList';
import AdminMailDetail from '@/components/admin/mails/AdminMailDetail';
import AdminDiscordMessagesList from '@/components/admin/discord/AdminDiscordMessagesList';
import AdminDiscordMessageDetail from '@/components/admin/discord/AdminDiscordMessageDetail';
import AdminTelegramMessagesList from '@/components/admin/telegram/AdminTelegramMessagesList';
import AdminTelegramMessageDetail from '@/components/admin/telegram/AdminTelegramMessageDetail';
import AdminSCNMessagesList from '@/components/admin/scn/AdminSCNMessagesList';
import AdminSCNMessageDetail from '@/components/admin/scn/AdminSCNMessageDetail';
import AdminPushNotificationsList from '@/components/admin/pushNotification/AdminPushNotificationsList';
import AdminPushNotificationDetail from '@/components/admin/pushNotification/AdminPushNotificationDetail';
import AdminUserQuestionsList from '@/components/admin/questions/AdminUserQuestionsList';
import AdminUserQuestionDetail from '@/components/admin/questions/AdminUserQuestionDetail';
import AdminStocksStatusLightsList from '@/components/admin/status-light/AdminStocksStatusLightsList';
import AdminSubscriptionsList from '@/components/admin/subscriptions/AdminSubscriptionsList';
import AdminEventsList from '@/components/admin/events/AdminEventsList';
import AdminEventSeriesDetail from '@/components/admin/events/AdminEventSeriesDetail';
import AdminUserDetailPage from '@/components/admin/users/AdminUserDetailPage';
import AdminRegistrationUrlGenerator from '@/components/admin/registration-url/AdminRegistrationUrlGenerator';
import ProtectedRoute from '@/components/common/ProtectedRoute';
import { ADMIN_TRADING_ROUTES } from '@/constants/routes';

/**
 * Admin-only application routes
 * All routes require admin privileges via ProtectedRoute with requireAdmin
 *
 * Note: This function returns Route elements and should be called as a function,
 * not used as a JSX component: {AdminRoutes()} not <AdminRoutes />
 */
export default function AdminRoutes() {
  return (
    <>
      {/* Admin Dashboard */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireEmployee loadingMessage="Lade Admin Dashboard...">
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* User Management - Employees can view but not edit */}
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requireEmployee loadingMessage="Lade Benutzerliste...">
            <AdminUsersList />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/users/:userId"
        element={
          <ProtectedRoute requireEmployee loadingMessage="Lade Benutzerdetails...">
            <AdminUserDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Chat Sessions */}
      <Route
        path="/admin/sessions"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Chat-Sitzungen...">
            <AdminSessionsList />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/chats/:sessionId"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Chat...">
            <AdminChatWindow />
          </ProtectedRoute>
        }
      />

      {/* LLM-Tools */}
      <Route
        path="/admin/llm-tools"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade LLM-Tools...">
            <AdminLLMToolsList />
          </ProtectedRoute>
        }
      />

      {/* Jobs */}
      <Route
        path="/admin/jobs"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Jobs...">
            <AdminJobsList />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/jobs/:jobType"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Job-Details...">
            <AdminJobDetail />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/jobs/:jobType/executions/:executionId/logs"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Job-Logs...">
            <AdminJobLogs />
          </ProtectedRoute>
        }
      />

      {/* Errors */}
      <Route
        path="/admin/errors"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Fehler...">
            <AdminErrorsList />
          </ProtectedRoute>
        }
      />

      {/* Symbols */}
      <Route
        path="/admin/symbols"
        element={
          <ProtectedRoute requireEmployee loadingMessage="Lade Symbole...">
            <AdminSymbolsList />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/symbols/:symbolId"
        element={
          <ProtectedRoute requireEmployee loadingMessage="Lade Symbol-Details...">
            <AdminSymbolDetail />
          </ProtectedRoute>
        }
      />

      {/* Stock Exchanges */}
      <Route
        path="/admin/stock-exchanges"
        element={
          <ProtectedRoute requireEmployee loadingMessage="Lade Börsen...">
            <AdminStockExchangesList />
          </ProtectedRoute>
        }
      />

      {/* Trading Recommendations */}
      {/* Redirect base path to core-recommendations */}
      <Route
        path="/admin/trading-recommendations"
        element={<Navigate to="/admin/trading-recommendations/core-recommendations" replace />}
      />

      {/* Tab-specific list routes */}
      <Route
        path="/admin/trading-recommendations/core-recommendations"
        element={
          <ProtectedRoute requireEmployee loadingMessage="Lade Kernstrategie-Tradingideen...">
            <AdminTradingRecommendationsList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/trading-recommendations/earnings-recommendations"
        element={
          <ProtectedRoute requireEmployee loadingMessage="Lade Earnings-Tradingideen...">
            <AdminTradingRecommendationsList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/trading-recommendations/swing-trades"
        element={
          <ProtectedRoute requireEmployee loadingMessage="Lade Swing-Trades...">
            <AdminTradingRecommendationsList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/trading-recommendations/long-term-trades"
        element={
          <ProtectedRoute requireEmployee loadingMessage="Lade Langzeit-Trades...">
            <AdminTradingRecommendationsList />
          </ProtectedRoute>
        }
      />

      {/* Admin detail page for Core recommendations */}
      <Route
        path={ADMIN_TRADING_ROUTES.CORE_RECOMMENDATION_DETAIL_PATTERN}
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Core-Empfehlung...">
            <AdminCoreRecommendationFullDetail />
          </ProtectedRoute>
        }
      />

      {/* Admin detail page for Earnings recommendations */}
      <Route
        path={ADMIN_TRADING_ROUTES.EARNINGS_RECOMMENDATION_DETAIL_PATTERN}
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Earnings-Empfehlung...">
            <AdminEarningsRecommendationFullDetail />
          </ProtectedRoute>
        }
      />

      {/* Admin detail page for Swing trades */}
      <Route
        path={ADMIN_TRADING_ROUTES.SWING_TRADE_DETAIL_PATTERN}
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Swing-Trade...">
            <AdminSwingTradeFullDetail />
          </ProtectedRoute>
        }
      />

      {/* Admin detail page for Long-term trades */}
      <Route
        path={ADMIN_TRADING_ROUTES.LONG_TERM_TRADE_DETAIL_PATTERN}
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Langzeit-Trade...">
            <AdminLongTermTradeFullDetail />
          </ProtectedRoute>
        }
      />

      {/* Courses - Employees can manage courses, modules, videos */}
      <Route
        path="/admin/courses"
        element={
          <ProtectedRoute requireEmployee loadingMessage="Lade Kurse...">
            <AdminCoursesList />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/courses/:courseId"
        element={
          <ProtectedRoute requireEmployee loadingMessage="Lade Kurs-Details...">
            <AdminCourseDetail />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/courses/:courseId/mail-sequence"
        element={
          <ProtectedRoute requireEmployee loadingMessage="Lade Mail-Sequenz...">
            <CourseMailSequence />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/courses/:courseId/modules/:moduleId"
        element={
          <ProtectedRoute requireEmployee loadingMessage="Lade Modul-Details...">
            <AdminModuleDetail />
          </ProtectedRoute>
        }
      />

      {/* Mails */}
      <Route
        path="/admin/mails"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade E-Mails...">
            <AdminMailsList />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/mails/:mailId"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade E-Mail-Details...">
            <AdminMailDetail />
          </ProtectedRoute>
        }
      />

      {/* Discord Messages */}
      <Route
        path="/admin/discord-messages"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Discord-Nachrichten...">
            <AdminDiscordMessagesList />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/discord-messages/:messageId"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Discord-Nachricht-Details...">
            <AdminDiscordMessageDetail />
          </ProtectedRoute>
        }
      />

      {/* Telegram Messages */}
      <Route
        path="/admin/telegram-messages"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Telegram-Nachrichten...">
            <AdminTelegramMessagesList />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/telegram-messages/:messageId"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Telegram-Nachricht-Details...">
            <AdminTelegramMessageDetail />
          </ProtectedRoute>
        }
      />

      {/* SCN Messages */}
      <Route
        path="/admin/scn-messages"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade SCN-Nachrichten...">
            <AdminSCNMessagesList />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/scn-messages/:messageId"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade SCN-Nachricht-Details...">
            <AdminSCNMessageDetail />
          </ProtectedRoute>
        }
      />

      {/* Push Notifications */}
      <Route
        path="/admin/push-notifications"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Push-Benachrichtigungen...">
            <AdminPushNotificationsList />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/push-notifications/:notificationId"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Push-Benachrichtigung-Details...">
            <AdminPushNotificationDetail />
          </ProtectedRoute>
        }
      />

      {/* User-Fragen */}
      <Route
        path="/admin/questions"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Fragen...">
            <AdminUserQuestionsList />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/questions/:questionId"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Frage...">
            <AdminUserQuestionDetail />
          </ProtectedRoute>
        }
      />

      {/* Stocks Status Lights */}
      <Route
        path="/admin/stocks-status-lights"
        element={
          <ProtectedRoute requireEmployee loadingMessage="Lade Börsen-Ampel Historie...">
            <AdminStocksStatusLightsList />
          </ProtectedRoute>
        }
      />

      {/* Subscriptions */}
      <Route
        path="/admin/subscriptions"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Subscriptions...">
            <AdminSubscriptionsList />
          </ProtectedRoute>
        }
      />

      {/* Registration URL Generator */}
      <Route
        path="/admin/registrierungs-links"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Generator...">
            <AdminRegistrationUrlGenerator />
          </ProtectedRoute>
        }
      />

      {/* Events */}
      <Route
        path="/admin/events"
        element={
          <ProtectedRoute requireEmployee loadingMessage="Lade Events...">
            <AdminEventsList />
          </ProtectedRoute>
        }
      />

      {/* Event Series - requireAdmin, every /event-series endpoint is PermAdmin */}
      <Route
        path="/admin/event-series/:seriesId"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Event-Serie...">
            <AdminEventSeriesDetail />
          </ProtectedRoute>
        }
      />

      {/* All Courses (System section) */}
      <Route
        path="/admin/all-courses"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade alle Kurse...">
            <AdminAllCoursesList />
          </ProtectedRoute>
        }
      />

      {/* All Modules (System section) */}
      <Route
        path="/admin/all-modules"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade alle Module...">
            <AdminAllModulesList />
          </ProtectedRoute>
        }
      />

      {/* All Videos (System section) */}
      <Route
        path="/admin/all-videos"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade alle Videos...">
            <AdminAllVideosList />
          </ProtectedRoute>
        }
      />

      {/* Tools (System section) */}
      <Route
        path="/admin/tools"
        element={<Navigate to="/admin/tools/test-messages" replace />}
      />
      <Route
        path="/admin/tools/test-messages"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Tools...">
            <AdminToolsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tools/resend-notifications"
        element={
          <ProtectedRoute requireAdmin loadingMessage="Lade Tools...">
            <AdminToolsPage />
          </ProtectedRoute>
        }
      />

    </>
  );
}
